import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { projectsService } from '@/features/projects/public';
import type { Project } from '@/features/projects/public';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';

export interface UseMyProjectsSearchResult {
  data: Project[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
}

/**
 * Searchable list of the projects the current user belongs to, for the convert-to-task
 * dialog's project picker.
 *
 * Lives here rather than in the projects feature because it is this dialog's need: a
 * picker fed by a fixed first page silently hides every project past it, so the field
 * searches server-side instead. Reaches the projects feature only through its public
 * API, and keys its cache under `notes` so it owns nothing of the other feature's key
 * space.
 *
 * @param search - Sent as `SearchTerm`; empty means "no filter"
 * @param pageIndex - Zero-based, converted to the API's 1-based page
 */
export function useMyProjectsSearch(
  search: string,
  pageIndex = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  options?: { includeArchived?: boolean; enabled?: boolean },
): UseMyProjectsSearchResult {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  const params = {
    SearchTerm: search || undefined,
    Page: pageIndex + 1,
    PageSize: pageSize,
    IncludeArchived: options?.includeArchived ?? false,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['notes', 'convert-to-task', 'projects', orgId ?? '', params] as const,
    queryFn: ({ signal }) => projectsService.getMyProjects(params, signal),
    staleTime: 60_000,
    enabled: !!orgId && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
  });

  return {
    data: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
  };
}
